import {
  RepairStatus,
  RepairVisit,
  RepairVisitPrecision,
  RepairVisitStatus
} from '../../../../../shared/definitions/repair';

/**
 * Pure helpers for the appointment ("visit") layer of a repair.
 *
 * The field-service standard (Dynamics bookings, Salesforce appointments,
 * Jobber visits): a job can have several visits and the job's high-level
 * status is *derived* from the active visit. We keep the existing
 * `RepairStatus` enum (no migration) and nudge it from visit outcomes.
 *
 * The helpers operate on a plain `RepairVisit[]` so they work regardless of
 * where the array is stored: manual repairs keep it on the Repair doc; a
 * work-request repair keeps it on the shared ServiceRequest (so the provider
 * sees/acts on it via the projection). Kept free of `Date.now()` / id
 * generation so it unit-tests cleanly — the caller passes `now` + a fresh id.
 */

/**
 * Visit statuses that mean "a future/ongoing appointment exists". A `proposed`
 * candidate slot is deliberately excluded — it is not a booked appointment
 * until the owner confirms it.
 */
export const ACTIVE_VISIT_STATUSES: readonly RepairVisitStatus[] = ['scheduled', 'confirmed', 'on_site'];

/** A single candidate slot put forward by the provider. */
export interface ProposedSlotInput {
  at: Date;
  precision?: RepairVisitPrecision;
}

export type VisitAction =
  | { kind: 'schedule'; at: Date; providerName?: string; note?: string; precision?: RepairVisitPrecision }
  | { kind: 'reschedule'; visitId: string; at: Date; precision?: RepairVisitPrecision }
  | { kind: 'confirm'; visitId: string }
  // Provider proposes one or more candidate slots (provider portal).
  | { kind: 'propose'; slots: ProposedSlotInput[]; providerName?: string; note?: string }
  // Owner confirms one proposed slot; sibling proposals are cancelled.
  | { kind: 'confirmSlot'; visitId: string }
  // Owner declines every outstanding proposed slot (asks for new ones).
  | { kind: 'declineSlots' }
  | { kind: 'cancel'; visitId: string }
  | { kind: 'onSite'; visitId: string }
  | { kind: 'noShow'; visitId: string }
  | { kind: 'complete'; visitId: string }
  | { kind: 'incomplete'; visitId: string };

export interface VisitActionContext {
  now: Date;
  /**
   * Base id for newly created visit(s). Used by `schedule` (single id) and
   * `propose` (base id suffixed with `-0`, `-1`, … per candidate slot).
   */
  newId?: string;
}

export interface VisitActionResult {
  visits: RepairVisit[];
  status: RepairStatus;
  /** `Date` to set, `null` to clear, `undefined` to leave untouched. */
  completedDate?: Date | null;
  /** Stable token for the activity log + i18n summary. */
  logKind: VisitAction['kind'];
}

/** Coerce a Firestore Timestamp / string / Date into a Date (or null). */
export function toVisitDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  const ts = value as { toDate?: () => Date };
  if (typeof ts.toDate === 'function') {
    try {
      return ts.toDate();
    } catch {
      return null;
    }
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/** Visits sorted by scheduled time ascending (stable, defensive on bad dates). */
export function sortedVisits(visits: readonly RepairVisit[] | null | undefined): RepairVisit[] {
  return [...(visits ?? [])].sort((a, b) => {
    const ta = toVisitDate(a.scheduledAt)?.getTime() ?? 0;
    const tb = toVisitDate(b.scheduledAt)?.getTime() ?? 0;
    return ta - tb;
  });
}

/** The appointment that still needs attention (scheduled / confirmed / on site). */
export function activeVisit(visits: readonly RepairVisit[] | null | undefined): RepairVisit | null {
  const active = sortedVisits(visits).filter((v) => ACTIVE_VISIT_STATUSES.includes(v.status));
  // Prefer an on-site one, else the soonest upcoming.
  return active.find((v) => v.status === 'on_site') ?? active[0] ?? null;
}

/**
 * i18n key suffix for a slot's precision, under `repairs.detail.visitPrecision.*`.
 * `exact` has no dedicated label (the formatted time speaks for itself).
 */
export function visitPrecisionLabelKey(precision: RepairVisitPrecision | undefined): string | null {
  switch (precision) {
    case 'am':
      return 'repairs.detail.visitPrecision.am';
    case 'pm':
      return 'repairs.detail.visitPrecision.pm';
    case 'day':
      return 'repairs.detail.visitPrecision.day';
    default:
      return null;
  }
}

/** Candidate slots proposed by the provider, awaiting owner confirmation. */
export function proposedVisits(visits: readonly RepairVisit[] | null | undefined): RepairVisit[] {
  return sortedVisits(visits).filter((v) => v.status === 'proposed');
}

/** True when the provider has proposed slots and none is yet confirmed. */
export function hasPendingProposal(visits: readonly RepairVisit[] | null | undefined): boolean {
  return !activeVisit(visits) && proposedVisits(visits).length > 0;
}

/** The most recent visit overall (for showing the last outcome). */
export function latestVisit(visits: readonly RepairVisit[] | null | undefined): RepairVisit | null {
  const all = sortedVisits(visits);
  return all.length ? all[all.length - 1] : null;
}

/** True when the last visit ended needing a follow-up ("repassage"). */
export function needsFollowUp(visits: readonly RepairVisit[] | null | undefined): boolean {
  if (activeVisit(visits)) return false;
  const last = latestVisit(visits);
  return !!last && (last.status === 'incomplete' || last.status === 'no_show');
}

const TERMINAL_REPAIR_STATUSES: readonly RepairStatus[] = [
  RepairStatus.Completed,
  RepairStatus.Resolved,
  RepairStatus.Canceled
];

/**
 * Apply a visit action, returning the new visits array + the derived repair
 * status. Never mutates the input. Unknown visitIds are a no-op (returns the
 * current state) so a stale click can't corrupt the array.
 */
export function applyVisitAction(
  currentVisits: readonly RepairVisit[] | null | undefined,
  currentStatus: RepairStatus,
  action: VisitAction,
  ctx: VisitActionContext
): VisitActionResult {
  const current = currentStatus;
  const visits = (currentVisits ?? []).map((v) => ({ ...v }));

  if (action.kind === 'schedule') {
    const visit: RepairVisit = {
      id: ctx.newId ?? `${ctx.now.getTime()}`,
      providerName: action.providerName,
      scheduledAt: action.at,
      status: 'scheduled',
      precision: action.precision ?? 'exact',
      proposedBy: 'owner',
      note: action.note,
      createdAt: ctx.now
    };
    return {
      visits: [...visits, visit],
      status: TERMINAL_REPAIR_STATUSES.includes(current) ? current : RepairStatus.Assigned,
      logKind: 'schedule'
    };
  }

  if (action.kind === 'propose') {
    const base = ctx.newId ?? `${ctx.now.getTime()}`;
    const proposals: RepairVisit[] = action.slots.map((slot, i) => ({
      id: `${base}-${i}`,
      providerName: action.providerName,
      scheduledAt: slot.at,
      status: 'proposed',
      precision: slot.precision ?? 'exact',
      proposedBy: 'provider',
      note: action.note,
      createdAt: ctx.now
    }));
    // A fresh proposal supersedes any earlier outstanding proposals.
    const withoutStale = visits.filter((v) => v.status !== 'proposed');
    return {
      visits: [...withoutStale, ...proposals],
      status: current,
      logKind: 'propose'
    };
  }

  if (action.kind === 'declineSlots') {
    const next = visits.map((v) =>
      v.status === 'proposed' ? { ...v, status: 'cancelled' as RepairVisitStatus, outcomeAt: ctx.now } : v
    );
    return { visits: next, status: current, logKind: 'declineSlots' };
  }

  const idx = visits.findIndex((v) => v.id === action.visitId);
  if (idx === -1) {
    return { visits, status: current, logKind: action.kind };
  }
  const visit = visits[idx];

  switch (action.kind) {
    case 'reschedule':
      visits[idx] = {
        ...visit,
        scheduledAt: action.at,
        status: 'scheduled',
        ...(action.precision ? { precision: action.precision } : {})
      };
      return { visits, status: RepairStatus.Assigned, logKind: 'reschedule' };
    case 'confirm':
      visits[idx] = { ...visit, status: 'confirmed' };
      return { visits, status: RepairStatus.Assigned, logKind: 'confirm' };
    case 'confirmSlot': {
      // Owner picks one proposed slot → it becomes the confirmed appointment;
      // every sibling proposed slot is cancelled.
      const next = visits.map((v, i) => {
        if (i === idx) return { ...v, status: 'confirmed' as RepairVisitStatus };
        if (v.status === 'proposed') return { ...v, status: 'cancelled' as RepairVisitStatus, outcomeAt: ctx.now };
        return v;
      });
      return { visits: next, status: RepairStatus.Assigned, logKind: 'confirmSlot' };
    }
    case 'cancel':
      visits[idx] = { ...visit, status: 'cancelled', outcomeAt: ctx.now };
      return { visits, status: RepairStatus.Assigned, logKind: 'cancel' };
    case 'onSite':
      visits[idx] = { ...visit, status: 'on_site' };
      return { visits, status: RepairStatus.InProgress, logKind: 'onSite' };
    case 'noShow':
      visits[idx] = { ...visit, status: 'no_show', outcomeAt: ctx.now };
      return { visits, status: RepairStatus.Assigned, logKind: 'noShow' };
    case 'complete':
      visits[idx] = { ...visit, status: 'completed', outcomeAt: ctx.now };
      return {
        visits,
        status: RepairStatus.Completed,
        completedDate: ctx.now,
        logKind: 'complete'
      };
    case 'incomplete':
      // "Repassage": the provider came but the job isn't finished. Close this
      // visit and drop the repair back to the scheduling step so the next
      // obvious action is booking a follow-up visit.
      visits[idx] = { ...visit, status: 'incomplete', outcomeAt: ctx.now };
      return { visits, status: RepairStatus.Assigned, logKind: 'incomplete' };
  }
}
